import { useState } from 'react'
import { Search, UserPlus, UserRound } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useClientSearchV2 } from '@/hooks/useClientSearchV2'
import { cn } from '@/lib/utils'

import type { WinnerImportCustomer, WinnerImportCustomerCandidate, WinnerImportCustomerDecision } from './winner-import.api'

type Props = {
  winner: WinnerImportCustomer
  candidates: WinnerImportCustomerCandidate[]
  decision: WinnerImportCustomerDecision
  onChange: (decision: WinnerImportCustomerDecision) => void
}

/** Existing customer (with the evidence Soly found) or a new one pre-filled from Winner. */
export function WinnerCustomerBlock({ winner, candidates, decision, onChange }: Props) {
  const [query, setQuery] = useState('')
  const { results } = useClientSearchV2({ q: query, take: 5 })
  const set = (patch: Partial<WinnerImportCustomerDecision>) => onChange({ ...decision, ...patch })

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Winner indique <span className="font-medium text-foreground">{winner.displayName}</span>
        {winner.winnerNumber && <> · client n° {winner.winnerNumber}</>}.
        {candidates.length > 0 ? ' Soly a trouvé un client qui lui ressemble.' : ' Aucun client semblable dans Soly.'}
      </p>

      <div className="grid gap-2" role="radiogroup" aria-label="Client">
        {candidates.map((c) => (
          <Choice
            key={c.customerId}
            testId={`winner-import-customer-candidate-${c.customerId}`}
            selected={decision.mode === 'existing' && decision.customerId === c.customerId}
            onSelect={() => set({ mode: 'existing', customerId: c.customerId, name: c.name })}
            icon={<UserRound className="h-4 w-4" />}
            title={c.name}
            detail={[c.city, c.email, c.phone, `${c.projects.length} chantier${c.projects.length > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
            badges={c.evidence}
          />
        ))}
        {decision.mode === 'existing' && decision.customerId && !candidates.some((c) => c.customerId === decision.customerId) && (
          <Choice
            testId="winner-import-customer-picked"
            selected
            onSelect={() => undefined}
            icon={<UserRound className="h-4 w-4" />}
            title={decision.name}
            detail="Client choisi dans Soly"
            badges={[]}
          />
        )}
        <Choice
          testId="winner-import-customer-new"
          selected={decision.mode === 'new'}
          onSelect={() => set({ mode: 'new', customerId: undefined, name: decision.mode === 'new' ? decision.name : winner.displayName })}
          icon={<UserPlus className="h-4 w-4" />}
          title="Créer un nouveau client"
          detail="Pré-rempli avec les informations de Winner"
          badges={[]}
        />
      </div>

      {decision.mode === 'new' && (
        <div className="grid gap-3 rounded-lg border bg-slate-50/60 p-4 sm:grid-cols-2">
          <div className="flex gap-2 sm:col-span-2">
            {[false, true].map((company) => (
              <button
                key={String(company)}
                type="button"
                data-testid={`winner-import-customer-type-${company ? 'company' : 'individual'}`}
                onClick={() => set({ isCompany: company })}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm',
                  decision.isCompany === company ? 'border-slate-900 bg-slate-900 text-white' : 'bg-background'
                )}
              >
                {company ? 'Société' : 'Particulier'}
              </button>
            ))}
          </div>
          <Field id="name" label={decision.isCompany ? 'Raison sociale' : 'Nom affiché'} value={decision.name} onChange={(v) => set({ name: v })} wide />
          {!decision.isCompany && <Field id="first" label="Prénom" value={decision.firstName} onChange={(v) => set({ firstName: v })} missing={!winner.firstName} />}
          {!decision.isCompany && <Field id="last" label="Nom" value={decision.lastName} onChange={(v) => set({ lastName: v })} />}
          <Field id="email" label="E-mail" value={decision.email} onChange={(v) => set({ email: v })} missing={!winner.email} />
          <Field id="phone" label="Téléphone" value={decision.phone} onChange={(v) => set({ phone: v })} missing={!winner.phone} />
          {decision.isCompany && <Field id="vat" label="N° TVA" value={decision.vatNumber} onChange={(v) => set({ vatNumber: v })} />}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="winner-import-customer-search" className="text-xs text-muted-foreground">Ou rattacher à un autre client Soly</Label>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            id="winner-import-customer-search"
            data-testid="winner-import-customer-search-input"
            className="pl-9"
            placeholder="Nom, e-mail, téléphone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {query.trim().length >= 2 && results.length > 0 && (
          <ul className="divide-y rounded-md border">
            {results.map((r) => (
              <li key={r.customerId ?? r.name}>
                <button
                  type="button"
                  data-testid={`winner-import-customer-search-result-${r.customerId}`}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => {
                    if (!r.customerId) return
                    set({ mode: 'existing', customerId: r.customerId, name: r.name ?? 'Client' })
                    setQuery('')
                  }}
                >
                  <span className="font-medium">{r.name}</span>
                  <span className="text-muted-foreground"> {r.contacts?.[0]?.email ?? ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

type ChoiceProps = {
  testId: string
  selected: boolean
  onSelect: () => void
  icon: React.ReactNode
  title: string
  detail: string
  badges: string[]
}

export function Choice({ testId, selected, onSelect, icon, title, detail, badges }: ChoiceProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900',
        selected ? 'border-slate-900 bg-slate-50' : 'hover:bg-slate-50/60'
      )}
    >
      <span className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', selected && 'border-slate-900')}>
        {selected && <span className="h-2 w-2 rounded-full bg-slate-900" />}
      </span>
      <span className="mt-0.5 text-slate-500">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{title}</span>
        <span className="block truncate text-sm text-muted-foreground">{detail}</span>
        {badges.length > 0 && (
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            {badges.map((b) => (
              <span key={b} className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700">{b}</span>
            ))}
          </span>
        )}
      </span>
    </button>
  )
}

function Field({ id, label, value, onChange, missing, wide }: {
  id: string; label: string; value: string; onChange: (v: string) => void; missing?: boolean; wide?: boolean
}) {
  return (
    <div className={cn('space-y-1', wide && 'sm:col-span-2')}>
      <Label htmlFor={`winner-import-customer-${id}`} className="flex items-center justify-between text-xs">
        {label}
        {missing && !value && <span className="font-normal text-muted-foreground">absent de Winner</span>}
      </Label>
      <Input
        id={`winner-import-customer-${id}`}
        data-testid={`winner-import-customer-${id}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}
